/**
 * Scrolls to a section by id, waiting for it to mount first.
 *
 * Pages are lazy-loaded chunks, so after an SPA navigation the target section
 * doesn't exist yet — a fixed setTimeout fires too early and the scroll
 * silently no-ops, leaving the user wherever the previous scroll ended.
 * This polls per animation frame until the element appears (or maxWaitMs
 * elapses), scrolls to it, then runs one settle pass to correct for layout
 * shifts caused by other lazy sections mounting above the target.
 */
export function scrollToSection(id: string, maxWaitMs = 5000): void {
  const start = performance.now();
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  const settle = (el: HTMLElement) => {
    const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    if (Math.abs(el.getBoundingClientRect().top - margin) > 40) {
      el.scrollIntoView({ behavior, block: "start" });
    }
  };

  const attempt = () => {
    // The prerendered fallback may contain the same ID but is hidden after JS.
    const el = Array.from(document.querySelectorAll<HTMLElement>(`[id="${CSS.escape(id)}"]`))
      .find(candidate => !candidate.closest('#seo-fallback'));
    if (!el) {
      if (performance.now() - start < maxWaitMs) requestAnimationFrame(attempt);
      return;
    }
    el.scrollIntoView({ behavior, block: "start" });
    window.setTimeout(() => settle(el), 900);
  };

  attempt();
}
